<template>
    <div class="chart-canvas" :style="{ height }">
        <canvas ref="canvas" role="img" :aria-label="ariaLabel"></canvas>
        <div v-if="loading" class="chart-canvas__state">
            <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
        </div>
        <div v-else-if="error" class="chart-canvas__state chart-canvas__state--error" role="alert">
            <i class="fas fa-triangle-exclamation" aria-hidden="true"></i>
            <span>{{ error }}</span>
        </div>
        <div v-else-if="isEmpty" class="chart-canvas__state">
            <span>{{ emptyText }}</span>
        </div>
    </div>
</template>
<script>
import Chart from 'chart.js';

// Grosor (px) del borde de las barras: lo bastante marcado para leerse con el relleno suave, sin comerse barras angostas.
const BAR_BORDER = 1;

/** `#RRGGBB` con transparencia (`rgba`). Cualquier otro formato se devuelve igual: sin relleno suave, pero no se rompe. */
function withAlpha(color, alpha) {
    const hex = /^#([0-9a-f]{6})$/i.exec(color || '');
    if (!hex) return color;
    const n = parseInt(hex[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * Gráfica reutilizable sobre Chart.js 2.x (MIT). Se actualiza en sitio cuando
 * cambian `labels` o `datasets`; solo se recrea al cambiar `type` u `options`.
 * `options` reemplaza claves enteras de las opciones base (p. ej. `scales`), y se
 * vigila en profundidad: pásalo como constante, no como objeto nuevo en cada render.
 *
 *   <ChartCanvas type="bar" :labels="labels" :datasets="datasets" :loading="loading" :error="error" />
 *
 * `datasets` son los de Chart.js sin color: el componente los pinta con la
 * paleta del tema de Vuetify (nada de hex en las vistas). En `doughnut` el
 * color va por rebanada; en `bar`/`line`, por serie.
 * Estados (cargando / error / vacío) se pintan encima, así cada widget falla
 * por separado.
 */
export default {
    name: 'ChartCanvas',
    props: {
        type: { type: String, default: 'bar', validator: (v) => ['bar', 'horizontalBar', 'line', 'doughnut'].includes(v) },
        labels: { type: Array, default: () => [] },
        datasets: { type: Array, default: () => [] },
        options: { type: Object, default: () => ({}) },
        loading: { type: Boolean, default: false },
        error: { type: String, default: '' },
        emptyText: { type: String, default: 'Sin datos en el periodo' },
        height: { type: String, default: '300px' },
        ariaLabel: { type: String, default: 'Gráfica' }
    },
    data() {
        return { chart: null };
    },
    computed: {
        isEmpty() {
            return !this.datasets.length || !this.labels.length;
        },
        palette() {
            const t = this.$vuetify.theme.currentTheme;
            return [t.primary, t.info, t.warning, t.success, t.accent, t.error, t.secondary].filter(Boolean);
        }
    },
    watch: {
        type() {
            this.render();
        },
        labels() {
            this.sync();
        },
        datasets: {
            deep: true,
            handler() {
                this.sync();
            }
        },
        options: {
            deep: true,
            handler() {
                this.render();
            }
        }
    },
    mounted() {
        this.render();
    },
    beforeDestroy() {
        this.destroy();
    },
    methods: {
        color(i) {
            return this.palette[i % this.palette.length];
        },
        // Color de la superficie del tema (claro u oscuro): separa las rebanadas de la dona y recorta los puntos.
        surface() {
            const value = this.$el && window.getComputedStyle(this.$el).getPropertyValue('--app-surface');
            return (value && value.trim()) || '#FFFFFF';
        },
        paint() {
            return this.datasets.map((ds, i) => {
                if (this.type === 'doughnut') {
                    const surface = this.surface();
                    return {
                        ...ds,
                        backgroundColor: ds.data.map((_, j) => this.color(j)),
                        borderColor: surface,
                        hoverBorderColor: surface,
                        borderWidth: 2
                    };
                }
                // `tone` (success, error...) fija el color de una serie con significado propio, p. ej. la merma.
                const c = this.$vuetify.theme.currentTheme[ds.tone] || this.color(i);
                if (this.type === 'line') {
                    return {
                        ...ds,
                        borderColor: c,
                        backgroundColor: c,
                        borderWidth: 2,
                        pointBackgroundColor: c,
                        pointBorderColor: this.surface(),
                        pointRadius: 3,
                        pointHoverRadius: 5,
                        fill: false,
                        lineTension: 0.25
                    };
                }
                // Barras: relleno suave y borde del color pleno. Una barra en 0 no lleva borde (sería una raya en el eje).
                return {
                    ...ds,
                    backgroundColor: withAlpha(c, 0.28),
                    hoverBackgroundColor: withAlpha(c, 0.5),
                    borderColor: c,
                    hoverBorderColor: c,
                    borderWidth: ds.data.map((value) => (Number(value) ? BAR_BORDER : 0)),
                    borderSkipped: false
                };
            });
        },
        baseOptions() {
            const common = { responsive: true, maintainAspectRatio: false, legend: { position: 'bottom' } };
            if (this.type === 'doughnut') return common;
            if (this.type === 'horizontalBar') {
                return {
                    ...common,
                    legend: { display: false },
                    tooltips: { mode: 'index', intersect: false },
                    scales: { xAxes: [{ ticks: { beginAtZero: true } }] }
                };
            }
            return {
                ...common,
                tooltips: { mode: 'index', intersect: false },
                scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] }
            };
        },
        render() {
            this.destroy();
            if (!this.$refs.canvas) return;
            this.chart = new Chart(this.$refs.canvas.getContext('2d'), {
                type: this.type,
                data: { labels: this.labels, datasets: this.paint() },
                options: { ...this.baseOptions(), ...this.options }
            });
        },
        sync() {
            if (!this.chart) return this.render();
            this.chart.data.labels = this.labels;
            this.chart.data.datasets = this.paint();
            this.chart.update();
        },
        destroy() {
            if (this.chart) {
                this.chart.destroy();
                this.chart = null;
            }
        }
    }
};
</script>
<style scoped>
.chart-canvas {
    position: relative;
    width: 100%;
}
.chart-canvas__state {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: var(--app-surface, transparent);
    opacity: 0.94;
    font-size: 0.875rem;
}
.chart-canvas__state--error {
    color: var(--v-error-base);
}
</style>
